import { useState, useEffect } from 'react';
import { db } from '@/lib/firebase';
import { doc, onSnapshot } from 'firebase/firestore';

export interface DayHours {
  isOpen: boolean;
  openTime: string;
  closeTime: string;
}

export interface BusinessHours {
  monday: DayHours;
  tuesday: DayHours;
  wednesday: DayHours;
  thursday: DayHours;
  friday: DayHours;
  saturday: DayHours;
  sunday: DayHours;
}

export interface BusinessInfo {
  instagram: string;
  facebook: string;
  tiktok: string;
  whatsapp: string; // Phone number without + or spaces for the link
  address: string;
  phone: string; // Formatted phone for display
  hours?: BusinessHours;
  forceClosed?: boolean;
}

const defaultHours: BusinessHours = {
  monday: { isOpen: false, openTime: '11:00', closeTime: '23:00' },
  tuesday: { isOpen: true, openTime: '11:00', closeTime: '23:00' },
  wednesday: { isOpen: true, openTime: '11:00', closeTime: '23:00' },
  thursday: { isOpen: true, openTime: '11:00', closeTime: '23:00' },
  friday: { isOpen: true, openTime: '11:00', closeTime: '23:00' },
  saturday: { isOpen: true, openTime: '11:00', closeTime: '23:00' },
  sunday: { isOpen: true, openTime: '11:00', closeTime: '23:00' },
};

const defaultInfo: BusinessInfo = {
  instagram: 'https://instagram.com/bucara__burger',
  facebook: 'https://www.facebook.com/profile.php?id=61589000696717',
  tiktok: 'https://tiktok.com/@bucara__burger',
  whatsapp: '19862173604',
  address: 'Boise, Idaho 83701',
  phone: '+1 (986) 217-3604',
  hours: defaultHours,
};

export function useBusinessInfo() {
  const [info, setInfo] = useState<BusinessInfo>(defaultInfo);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsub = onSnapshot(doc(db, 'settings', 'business_info'), (docSnap) => {
      if (docSnap.exists()) {
        const data = docSnap.data() as BusinessInfo;
        setInfo({ ...defaultInfo, ...data, hours: data.hours || defaultInfo.hours });
      }
      setLoading(false);
    });

    return () => unsub();
  }, []);

  const isFullLink = info.whatsapp?.includes('wa.me') || info.whatsapp?.startsWith('http');
  const waLink = isFullLink 
    ? info.whatsapp 
    : `https://wa.me/${info.whatsapp ? info.whatsapp.replace(/\\D/g, '') : ''}?text=Hola%2C%20quiero%20hacer%20un%20pedido.%20%2F%20Hello%2C%20I%20want%20to%20place%20an%20order.`;

  let isOpenNow = false;
  if (!info.forceClosed && info.hours) {
    const now = new Date();
    const day = now.getDay(); // 0 = Sun
    const days: (keyof BusinessHours)[] = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const todayKey = days[day];
    const todayHours = info.hours[todayKey];
    
    if (todayHours && todayHours.isOpen) {
      const currentMinutes = now.getHours() * 60 + now.getMinutes();
      const [openH, openM] = (todayHours.openTime || '00:00').split(':').map(Number);
      const [closeH, closeM] = (todayHours.closeTime || '00:00').split(':').map(Number);
      
      const openMinutes = openH * 60 + (openM || 0);
      const closeMinutes = closeH * 60 + (closeM || 0);
      
      if (closeMinutes < openMinutes) {
        // Closes past midnight
        isOpenNow = currentMinutes >= openMinutes || currentMinutes <= closeMinutes;
      } else {
        isOpenNow = currentMinutes >= openMinutes && currentMinutes <= closeMinutes;
      }
    }
  }

  return { info, loading, waLink, isOpenNow };
}
